import React, { useState, useRef, useEffect } from 'react';

// Difficulty levels. The `max` value bounds both the operands and the result
// (for additions a + b ≤ max; for subtractions a ≤ max with 0 ≤ b ≤ a so the
// result is never negative).
const LEVELS = [
  { id: 1, title: 'Nível 1', description: 'Até 20', max: 20 },
  { id: 2, title: 'Nível 2', description: 'Até 100', max: 100 },
  { id: 3, title: 'Nível 3', description: 'Até 500', max: 500 },
  { id: 4, title: 'Nível 4', description: 'Até 1000', max: 1000 },
];

const generateQuestion = (max) => {
  const isAddition = Math.random() < 0.5;

  if (isAddition) {
    // a + b ≤ max, both ≥ 0
    const a = Math.floor(Math.random() * (max + 1));
    const b = Math.floor(Math.random() * (max - a + 1));
    return { a, b, op: '+', answer: a + b };
  }

  // a ≤ max, 0 ≤ b ≤ a so the result stays non-negative
  const a = Math.floor(Math.random() * (max + 1));
  const b = Math.floor(Math.random() * (a + 1));
  return { a, b, op: '−', answer: a - b };
};

const AdditionsSubtractions = () => {
  const [level, setLevel] = useState(null);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'incorrect'
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const inputRef = useRef(null);
  const nextButtonRef = useRef(null);

  // Move focus to whichever element accepts the next keypress: the input
  // while the user is entering an answer, and the "Próxima" button once
  // feedback is shown (so pressing Enter advances to the next question on
  // both correct and incorrect responses — the input is disabled while
  // feedback is up and so doesn't receive key events itself).
  useEffect(() => {
    if (feedback === null && question !== null && inputRef.current) {
      inputRef.current.focus();
    } else if (feedback !== null && nextButtonRef.current) {
      nextButtonRef.current.focus();
    }
  }, [question, feedback]);

  // Auto-advance after a correct answer so the activity stays in flow.
  useEffect(() => {
    if (feedback !== 'correct') return;
    const timeoutId = setTimeout(() => nextQuestion(), 900);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const startLevel = (selected) => {
    setLevel(selected);
    setQuestion(generateQuestion(selected.max));
    setUserAnswer('');
    setFeedback(null);
    setScore({ correct: 0, total: 0 });
  };

  const checkAnswer = () => {
    if (feedback !== null || question === null) return;
    const trimmed = userAnswer.trim();
    if (trimmed === '') return;
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed)) return;

    const isCorrect = parsed === question.answer;
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    setScore((s) => ({
      correct: s.correct + (isCorrect ? 1 : 0),
      total: s.total + 1,
    }));
  };

  const nextQuestion = () => {
    setQuestion(generateQuestion(level.max));
    setUserAnswer('');
    setFeedback(null);
  };

  const changeLevel = () => {
    setLevel(null);
    setQuestion(null);
    setUserAnswer('');
    setFeedback(null);
    setScore({ correct: 0, total: 0 });
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (feedback === 'incorrect') {
      nextQuestion();
    } else if (feedback === null) {
      checkAnswer();
    }
  };

  // Level selection screen — shown before any question is asked.
  if (level === null) {
    return (
      <div className="game-container">
        <h1 className="game-title">Adições e Subtrações</h1>
        <h2 className="config-subtitle">Escolhe o nível</h2>
        <div className="game-menu">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              className="menu-button"
              onClick={() => startLevel(l)}
            >
              {l.title} — {l.description}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <h1 className="game-title">Adições e Subtrações</h1>
      <div className="game-status">
        {level.title} ({level.description}) · Pontuação: {score.correct} / {score.total}
      </div>

      <div className="math-equation">
        <span className="math-operand">{question.a}</span>
        <span className="math-operator">{question.op}</span>
        <span className="math-operand">{question.b}</span>
        <span className="math-operator">=</span>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className={`math-answer-input ${feedback || ''}`}
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={handleKeyDown}
          disabled={feedback !== null}
          aria-label="Resposta"
        />
      </div>

      <div className={`math-feedback ${feedback || ''}`}>
        {feedback === 'correct' && 'Correto!'}
        {feedback === 'incorrect' &&
          `Errado. ${question.a} ${question.op} ${question.b} = ${question.answer}.`}
      </div>

      <div className="game-buttons">
        {feedback === null ? (
          <button className="new-game-button" onClick={checkAnswer}>
            Verificar
          </button>
        ) : (
          <button
            ref={nextButtonRef}
            className="new-game-button"
            onClick={nextQuestion}
          >
            Próxima
          </button>
        )}
        <button className="new-game-button" onClick={changeLevel}>
          Mudar Nível
        </button>
      </div>
    </div>
  );
};

export default AdditionsSubtractions;
